import { useEffect, useRef } from "react";
import { createHeaders } from "@/data-access/core/authenticatedFetch";
import { dataServiceBaseUrl } from "@/data-access/core/constants";
import useAuth from "@/store/auth.slice";

const DEFAULT_DEBOUNCE_MS = 1000;

export interface UseDebouncedPersistWithKeepaliveFlushConfig<TPending> {
  loggedIn: boolean;
  scheduleDeps: readonly unknown[];
  resolvePending: () => TPending | null;
  persistKey: (pending: TPending) => string;
  isAlreadyCached: (pending: TPending) => boolean;
  persist: (pending: TPending) => Promise<void>;
  shouldFlush?: (pending: TPending) => boolean;
  flushQueryString: string;
  flushVariables: (pending: TPending) => Record<string, unknown>;
  onKeepaliveFlushSuccess: (pending: TPending) => void;
  flushErrorMessage: string;
  debounceMs?: number;
}

/**
 * Debounces backend writes and flushes the latest pending value with a keepalive
 * `fetch` when the tab becomes hidden.
 */
export const useDebouncedPersistWithKeepaliveFlush = <TPending>({
  loggedIn,
  scheduleDeps,
  resolvePending,
  persistKey,
  isAlreadyCached,
  persist,
  shouldFlush = () => true,
  flushQueryString,
  flushVariables,
  onKeepaliveFlushSuccess,
  flushErrorMessage,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDebouncedPersistWithKeepaliveFlushConfig<TPending>): void => {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<TPending | null>(null);
  const lastPersistedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loggedIn) return;

    const pending = resolvePending();
    if (!pending) return;

    pendingRef.current = pending;

    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const toPersist = pendingRef.current;
      pendingRef.current = null;
      if (!toPersist) return;

      const key = persistKey(toPersist);
      if (lastPersistedKeyRef.current === key) return;
      if (isAlreadyCached(toPersist)) {
        lastPersistedKeyRef.current = key;
        return;
      }

      lastPersistedKeyRef.current = key;

      void persist(toPersist).catch(() => {
        lastPersistedKeyRef.current = null;
      });
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [loggedIn, ...scheduleDeps]);

  useEffect(() => {
    if (!loggedIn) return;

    const flushPending = () => {
      if (document.visibilityState !== "hidden") return;

      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      const toPersist = pendingRef.current;
      pendingRef.current = null;
      if (!toPersist || !shouldFlush(toPersist)) return;

      const key = persistKey(toPersist);
      if (lastPersistedKeyRef.current === key) return;
      if (isAlreadyCached(toPersist)) {
        lastPersistedKeyRef.current = key;
        return;
      }

      const token = useAuth.getState().accessToken;
      if (!token) return;

      lastPersistedKeyRef.current = key;

      void createHeaders("POST", token)
        .then((headers) =>
          fetch(`${dataServiceBaseUrl}/graphql/`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              query: flushQueryString,
              variables: flushVariables(toPersist),
            }),
            keepalive: true,
          }),
        )
        .then(() => {
          onKeepaliveFlushSuccess(toPersist);
        })
        .catch((error) => {
          console.warn(flushErrorMessage, error);
          lastPersistedKeyRef.current = null;
        });
    };

    document.addEventListener("visibilitychange", flushPending);

    return () => {
      document.removeEventListener("visibilitychange", flushPending);
    };
  }, [loggedIn]);
};
